import clsx from 'clsx'
import {
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton
} from 'react-share'

import Link from '@/atoms/Link/Link'
import InfluencerImage from './InfluencerImage'
import { getFormatRgbaColor } from '@/utils/functions/utils'

/* assets */
import { ReactComponent as InstagramIcon } from 'public/assets/influencer/icons/social/Instagram.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/influencer/icons/social/Twitter.svg'
import { ReactComponent as LinkedInIcon } from 'public/assets/influencer/icons/social/Linkedin.svg'
import { ReactComponent as WhatsAppIcon } from 'public/assets/influencer/icons/social/WhatsApp.svg'
import { ReactComponent as ArrowIcon } from 'public/assets/icons/chevron-left.svg'
import { ReactComponent as ArrowLeftIcon } from 'public/assets/icons/feather/arrow-left.svg'
import { ReactComponent as ShareIcon } from 'public/assets/icons/feather/share-2.svg'

interface Props {
  image?: StrapiImageFile
  name: string
  designation: string
  themeColor: string
  links: SocialLinks
  slug: string
  nextSlug?: string
  previousSlug?: string
}

type SocialLinks = {
  instagram?: string
  twitter?: string
  linkedin?: string
}

function InfluencerDetailsHero({
  name,
  designation,
  image,
  themeColor,
  links,
  slug,
  nextSlug,
  previousSlug
}: Props): JSX.Element {
  return (
    <section
      style={{ backgroundColor: getFormatRgbaColor(themeColor) }}
      className="py-8 lg:py-16 lg:px-28 mb-10 lg:mb-16"
    >
      <div className="max-width-container">
        <Navigation slug={slug} className="mb-14 lg:mb-2" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center | relative | px-8 lg:px-14">
          <div className="text-center | lg:order-last">
            <h1 className="h1--alt font-medium line-clamp-2 | pb-2 lg:pb-4">
              {name}
            </h1>
            <p className="text-paragraph-4 lg:text-paragraph-2 font-medium line-clamp-3 | mb-3 lg:mb-6">
              {designation}
            </p>
            <img
              src="/assets/influencer/icons/text-decoration.svg"
              alt=""
              className="mx-auto mb-6 lg:mb-10"
            />
            <SocialLinks links={links} className="hidden | md:block" />
          </div>

          <div className="lg:order-first | mx-auto space-y-5">
            <InfluencerImage image={image} themeColor={themeColor} />
            <SocialLinks links={links} className="md:hidden" />
          </div>

          {previousSlug && (
            <Link
              href={previousSlug}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 | bg-[#431FD0] | rounded-full | w-8 h-8 lg:w-14 lg:h-14"
            >
              <div className="flex items-center justify-center | h-full">
                <ArrowIcon
                  className="text-white | w-4 h-4 lg:w-6 lg:h-6"
                  viewBox="0 0 27 27"
                />
              </div>
            </Link>
          )}
          {nextSlug && (
            <Link
              href={nextSlug}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 | bg-[#431FD0] | rounded-full | w-8 h-8 lg:w-14 lg:h-14"
            >
              <div className="flex items-center justify-center | h-full | transform rotate-180">
                <ArrowIcon
                  className="text-white | w-4 h-4 lg:w-6 lg:h-6"
                  viewBox="0 0 27 27"
                />
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}

export function Navigation(props: { slug: string; className?: string }) {
  const url = `https://www.peppercontent.io${props.slug}`

  const handleShareLink = async () => {
    const shareData = {
      title: 'Pepper Content',
      url: props.slug
    }

    try {
      await navigator.share(shareData)
    } catch (e) {
      console.error(`navigator share error: ${e}`)
    }
  }

  return (
    <nav
      className={clsx(
        'flex items-center justify-between md:justify-end | space-x-10',
        props.className
      )}
    >
      <Link
        href="/global-marketing-leaders/"
        className="text-small lg:text-paragraph-3 | flex items-center gap-1.5"
      >
        <ArrowLeftIcon className="w-4 h-4" viewBox="0 0 24 24" />
        Go back to homepage
      </Link>

      <div className="relative">
        <button
          className="peer | text-small lg:text-paragraph-3 | flex items-center gap-1.5"
          onClick={handleShareLink}
        >
          Share via
          <ShareIcon className="w-4 h-4" viewBox="0 0 24 24" />
        </button>

        <div
          className="peer-focus:opacity-100 peer-active:opacity-100 focus-within:opacity-100 opacity-0 | flex items-center | absolute right-0 top-full | rounded-full | px-2"
          style={{ background: 'rgba(9, 9, 9, 0.45)' }}
        >
          <LinkedinShareButton url={url} className="!p-2">
            <LinkedInIcon color="white" className="h-4 w-4" />
          </LinkedinShareButton>
          <TwitterShareButton url={url} className="!p-2">
            <TwitterIcon color="white" className="h-4 w-4" />
          </TwitterShareButton>
          <WhatsappShareButton url={url} className="!p-2">
            <WhatsAppIcon color="white" className="h-4 w-4" />
          </WhatsappShareButton>
        </div>
      </div>
    </nav>
  )
}

function SocialLinks(props: { links: SocialLinks; className: string }) {
  return (
    <div className={props.className}>
      <div className="flex justify-center | gap-6 lg:gap-7">
        {props.links.instagram && (
          <Link
            href={props.links.instagram}
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon color="#313131" className="h-6 w-6 lg:w-7 lg:h-7" />
          </Link>
        )}
        {props.links.twitter && (
          <Link
            href={props.links.twitter}
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwitterIcon color="#313131" className="h-6 w-6 lg:w-7 lg:h-7" />
          </Link>
        )}
        {props.links.linkedin && (
          <Link
            href={props.links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon color="#313131" className="h-6 w-6 lg:w-7 lg:h-7" />
          </Link>
        )}
      </div>
    </div>
  )
}

export default InfluencerDetailsHero
